/**
 * 创建群组页面 - 参考 openlink.chat 设计风格
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createGroup } from '../../api/groups';
import { toast } from 'sonner';

const createGroupSchema = z.object({
  group_name: z.string().min(2, '群组名称至少 2 位').max(20, '群组名称最多 20 位'),
  description: z.string().max(100, '描述最多 100 字').optional(),
  platform: z.string().optional(),
  external_group_id: z.string().optional(),
});

type CreateGroupForm = z.infer<typeof createGroupSchema>;

export default function CreateGroup() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateGroupForm>({
    resolver: zodResolver(createGroupSchema),
  });

  const onSubmit = async (data: CreateGroupForm) => {
    setIsLoading(true);
    try {
      const result = await createGroup(data);
      toast.success('群组创建成功！');
      navigate(`/groups/${result.id}`);
    } catch (error: any) {
      // 错误已在 axios 拦截器中处理
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-8">
        <a href="/dashboard" className="text-gray-400 hover:text-gray-600" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </a>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">创建群组</h1>
          <p className="text-gray-500 mt-1">创建一个新的群组来管理你的机器人</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">群组信息</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* 群组名称 */}
          <div>
            <label htmlFor="group_name" className="block text-sm font-medium text-gray-700 mb-2">
              群组名称 <span className="text-red-500">*</span>
            </label>
            <input
              id="group_name"
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                errors.group_name ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('group_name')}
              placeholder="例如：我的机器人集群"
            />
            {errors.group_name && (
              <p className="mt-1 text-sm text-red-600">{errors.group_name.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">2-20 个字符</p>
          </div>

          {/* 平台选择 */}
          <div>
            <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-2">
              平台
            </label>
            <select
              id="platform"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                errors.platform ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('platform')}
            >
              <option value="">选择平台</option>
              <option value="dingtalk">钉钉</option>
              <option value="feishu">飞书</option>
              <option value="wechat">企业微信</option>
              <option value="other">其他</option>
            </select>
            {errors.platform && (
              <p className="mt-1 text-sm text-red-600">{errors.platform.message}</p>
            )}
          </div>

          {/* 外部群组 ID */}
          <div>
            <label htmlFor="external_group_id" className="block text-sm font-medium text-gray-700 mb-2">
              外部群组 ID（可选）
            </label>
            <input
              id="external_group_id"
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                errors.external_group_id ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('external_group_id')}
              placeholder="例如：钉钉群 ID"
            />
            {errors.external_group_id && (
              <p className="mt-1 text-sm text-red-600">{errors.external_group_id.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">绑定真实的 IM 群组（可选）</p>
          </div>

          {/* 描述 */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              描述（可选）
            </label>
            <textarea
              id="description"
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('description')}
              placeholder="简单描述这个群组的用途"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          {/* 提交按钮 */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-lobster-red text-white py-3 rounded-lg font-medium hover:bg-lobster-red/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '创建中...' : '创建群组'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              取消
            </button>
          </div>
        </form>
      </div>

      {/* 提示信息 */}
      <div className="mt-6 p-6 bg-blue-50 rounded-xl border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-3">使用建议</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start"><span className="mr-2">•</span><span>每个账号最多创建 3 个群组</span></li>
          <li className="flex items-start"><span className="mr-2">•</span><span>每个群组最多添加 9 个机器人</span></li>
          <li className="flex items-start"><span className="mr-2">•</span><span>创建后可以随时修改群组信息</span></li>
          <li className="flex items-start"><span className="mr-2">•</span><span>删除群组会移除所有关联的机器人</span></li>
        </ul>
      </div>
    </div>
  );
}
